import React, { useMemo, useState } from "react";
import "./ProjectTasksModal.css";
import { Project } from "../models/Project";
import { ProjectTask, TaskStatus, TASK_STATUS_LABELS } from "../types";
import ExcelJS from "exceljs";

interface ProjectTasksModalProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onSave: (tasks: ProjectTask[]) => void;
  availableProjects?: Project[];
  onMoveTask?: (task: ProjectTask, targetProjectId: number) => void;
}

const makeTaskId = () =>
  `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toWeekLabel = (dateStr: string): string => {
  if (!dateStr) return "No week";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseIsoLocalDate = (dateStr: string): Date | null => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

const toMonthYearLabel = (date: Date): string =>
  date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

const toWeekRangeLabel = (weekStart: Date): string => {
  const weekEnd = addDays(weekStart, 6);
  const startDay = String(weekStart.getDate()).padStart(2, "0");
  const startMonth = String(weekStart.getMonth() + 1).padStart(2, "0");
  const endDay = String(weekEnd.getDate()).padStart(2, "0");
  const endMonth = String(weekEnd.getMonth() + 1).padStart(2, "0");
  return `${startDay}/${startMonth}-${endDay}/${endMonth}`;
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const parseWeekDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parsed = parseIsoLocalDate(dateStr);
  if (!parsed) return null;
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const toMonday = (date: Date): Date => {
  const monday = new Date(date);
  monday.setHours(0, 0, 0, 0);
  const day = monday.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diffToMonday);
  return monday;
};

const toMondayIso = (dateStr: string): string => {
  const parsed = parseWeekDate(dateStr);
  if (!parsed) return dateStr;
  return toIsoDate(toMonday(parsed));
};

const normalizeTaskWeekStartToMonday = (task: ProjectTask): ProjectTask => ({
  ...task,
  weekStart: toMondayIso(task.weekStart),
  status: task.status || TaskStatus.TODO,
});

const diffWholeWeeks = (from: Date, to: Date): number =>
  Math.round((to.getTime() - from.getTime()) / WEEK_MS);

type TaskCellAction =
  | "set-initial"
  | "extend-backward"
  | "shorten"
  | "extend-forward";

type WeekEditMenuState = {
  taskId: string;
  week: Date;
  x: number;
  y: number;
} | null;

const isTaskActiveInWeek = (task: ProjectTask, weekDate: Date): boolean => {
  const start = parseWeekDate(task.weekStart);
  if (!start) return false;

  const durationWeeks = Math.max(1, task.weekCount || 1);
  const endExclusive = addDays(start, durationWeeks * 7);
  return weekDate >= start && weekDate < endExclusive;
};

const getTaskCellAction = (task: ProjectTask, weekDate: Date): TaskCellAction => {
  const start = parseWeekDate(task.weekStart);
  if (!start) return "set-initial";

  const safeWeeks = Math.max(1, task.weekCount || 1);
  const endInclusive = addDays(start, (safeWeeks - 1) * 7);

  if (weekDate < start) return "extend-backward";
  if (weekDate <= endInclusive) return "shorten";
  return "extend-forward";
};

const getTaskCellTitle = (task: ProjectTask, weekDate: Date): string => {
  const weekText = toWeekLabel(toIsoDate(weekDate));
  const action = getTaskCellAction(task, weekDate);

  if (action === "set-initial")
    return `Click to choose Start here / End here at ${weekText}`;
  if (action === "extend-backward")
    return `Click to choose Start here / End here at ${weekText}`;
  if (action === "shorten")
    return `Click to choose Start here / End here at ${weekText}`;
  return `Click to choose Start here / End here at ${weekText}`;
};

const ProjectTasksModal: React.FC<ProjectTasksModalProps> = ({
  project,
  isOpen,
  onClose,
  onSave,
  availableProjects = [],
  onMoveTask,
}) => {
  const [tasks, setTasks] = useState<ProjectTask[]>(project.tasks || []);
  const [weekEditMenu, setWeekEditMenu] = useState<WeekEditMenuState>(null);
  const [taskMoveTargets, setTaskMoveTargets] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (isOpen) {
      setTasks((project.tasks || []).map(normalizeTaskWeekStartToMonday));
      setWeekEditMenu(null);
      setTaskMoveTargets({});
    }
  }, [isOpen, project]);

  React.useEffect(() => {
    if (!onMoveTask) return;

    setTaskMoveTargets((prev) => {
      const next = { ...prev };
      for (const task of tasks) {
        if (!next[task.id]) {
          const defaultTarget = availableProjects[0]?.id;
          if (defaultTarget !== undefined) {
            next[task.id] = String(defaultTarget);
          }
        }
      }
      return next;
    });
  }, [availableProjects, onMoveTask, tasks]);

  const scheduleWeeks = useMemo(() => {
    const start = project.startDate ? new Date(project.startDate) : null;
    const end = project.endDate ? new Date(project.endDate) : null;

    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return [] as Date[];
    }

    const weeks: Date[] = [];
    let cursor = toMonday(start);

    while (cursor <= end) {
      weeks.push(new Date(cursor));
      cursor = addDays(cursor, 7);
    }

    return weeks;
  }, [project.startDate, project.endDate]);

  const monthGroups = useMemo(() => {
    const groups: Array<{ label: string; startIndex: number; span: number }> = [];
    if (scheduleWeeks.length === 0) return groups;

    let startIndex = 0;
    while (startIndex < scheduleWeeks.length) {
      const seed = scheduleWeeks[startIndex];
      const month = seed.getMonth();
      const year = seed.getFullYear();
      let endIndex = startIndex;

      while (
        endIndex + 1 < scheduleWeeks.length &&
        scheduleWeeks[endIndex + 1].getMonth() === month &&
        scheduleWeeks[endIndex + 1].getFullYear() === year
      ) {
        endIndex += 1;
      }

      groups.push({
        label: toMonthYearLabel(seed),
        startIndex,
        span: endIndex - startIndex + 1,
      });

      startIndex = endIndex + 1;
    }

    return groups;
  }, [scheduleWeeks]);

  const addTask = () => {
    const baseDate = project.startDate
      ? new Date(project.startDate)
      : new Date();
    const defaultWeek = toIsoDate(toMonday(baseDate));
    setTasks((prev) => [
      ...prev,
      {
        id: makeTaskId(),
        name: "",
        weekStart: defaultWeek,
        weekCount: 1,
        status: TaskStatus.TODO,
      },
    ]);
  };

  const updateTask = (id: string, updates: Partial<ProjectTask>) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
    );
  };

  const openWeekEditMenu = (
    taskId: string,
    clickedWeek: Date,
    x: number,
    y: number,
  ) => {
    setWeekEditMenu({ taskId, week: toMonday(clickedWeek), x, y });
  };

  const applyStartHere = (taskId: string, clickedWeek: Date) => {
    const clicked = toMonday(clickedWeek);

    setTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) return task;

        const start = parseWeekDate(task.weekStart);
        if (!start) {
          return {
            ...task,
            weekStart: toIsoDate(clicked),
            weekCount: 1,
          };
        }

        const safeWeeks = Math.max(1, task.weekCount || 1);
        const endInclusive = addDays(start, (safeWeeks - 1) * 7);

        if (clicked > endInclusive) {
          return {
            ...task,
            weekStart: toIsoDate(clicked),
            weekCount: 1,
          };
        }

        return {
          ...task,
          weekStart: toIsoDate(clicked),
          weekCount: Math.max(1, diffWholeWeeks(clicked, endInclusive) + 1),
        };
      }),
    );
    setWeekEditMenu(null);
  };

  const applyEndHere = (taskId: string, clickedWeek: Date) => {
    const clicked = toMonday(clickedWeek);

    setTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) return task;

        const start = parseWeekDate(task.weekStart);
        if (!start || clicked < start) {
          return {
            ...task,
            weekStart: toIsoDate(clicked),
            weekCount: 1,
          };
        }

        return {
          ...task,
          weekCount: Math.max(1, diffWholeWeeks(start, clicked) + 1),
        };
      }),
    );
    setWeekEditMenu(null);
  };

  const deleteTask = (id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const moveTaskToProject = (task: ProjectTask) => {
    if (!onMoveTask) return;

    const targetProjectId = Number(taskMoveTargets[task.id]);
    if (!taskMoveTargets[task.id] || Number.isNaN(targetProjectId)) {
      alert("Choose a project to move the task to.");
      return;
    }

    onMoveTask(task, targetProjectId);
    setTasks((prev) => prev.filter((candidate) => candidate.id !== task.id));
    setTaskMoveTargets((prev) => {
      const next = { ...prev };
      delete next[task.id];
      return next;
    });
  };

  const exportTasksToXlsx = async () => {
    try {
      const staticHeaders = [
        "Tâche",
        "En charge",
        "Parties prenantes",
        "Échéance",
        "Statut",
      ];
      const staticColsCount = staticHeaders.length;
      const totalCols = staticColsCount + scheduleWeeks.length;

      const lastDate =
        scheduleWeeks.length > 0
          ? addDays(
              scheduleWeeks[scheduleWeeks.length - 1],
              6,
            ).toLocaleDateString("fr-FR")
          : "";

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Planning", {
        views: [{ state: "frozen", xSplit: staticColsCount, ySplit: 2 }],
      });

      const headerMonthRowValues = [
        ...staticHeaders,
        ...scheduleWeeks.map((week) => toMonthYearLabel(week)),
      ];
      const headerWeekRowValues = [
        ...staticHeaders.map(() => ""),
        ...scheduleWeeks.map((week) => toWeekRangeLabel(week)),
      ];

      worksheet.addRow(headerMonthRowValues);
      worksheet.addRow(headerWeekRowValues);

      for (let c = 1; c <= staticColsCount; c += 1) {
        worksheet.mergeCells(1, c, 2, c);
      }

      if (scheduleWeeks.length > 0) {
        let groupStart = 0;
        while (groupStart < scheduleWeeks.length) {
          const month = scheduleWeeks[groupStart].getMonth();
          const year = scheduleWeeks[groupStart].getFullYear();
          let groupEnd = groupStart;

          while (
            groupEnd + 1 < scheduleWeeks.length &&
            scheduleWeeks[groupEnd + 1].getMonth() === month &&
            scheduleWeeks[groupEnd + 1].getFullYear() === year
          ) {
            groupEnd += 1;
          }

          const startCol = staticColsCount + groupStart + 1;
          const endCol = staticColsCount + groupEnd + 1;

          if (endCol > startCol) {
            worksheet.mergeCells(1, startCol, 1, endCol);
          }

          groupStart = groupEnd + 1;
        }
      }

      tasks.forEach((task) => {
        const rowValues = [
          task.name || "",
          "",
          "",
          lastDate,
          task.status || "",
          ...scheduleWeeks.map(() => ""),
        ];
        const row = worksheet.addRow(rowValues);

        for (let i = 0; i < scheduleWeeks.length; i += 1) {
          if (isTaskActiveInWeek(task, scheduleWeeks[i])) {
            const cell = row.getCell(staticColsCount + i + 1);
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF16A34A" },
            };
          }
        }
      });

      worksheet.columns = Array.from({ length: totalCols }, (_, index) => {
        if (index === 0) return { width: 34 };
        if (index === 1) return { width: 18 };
        if (index === 2) return { width: 22 };
        if (index === 3) return { width: 14 };
        if (index === 4) return { width: 12 };
        return { width: 11 };
      });

      worksheet.getRow(1).height = 24;
      worksheet.getRow(2).height = 22;

      const headerFill = {
        type: "pattern" as const,
        pattern: "solid" as const,
        fgColor: { argb: "FF2563EB" },
      };

      for (let r = 1; r <= 2; r += 1) {
        const row = worksheet.getRow(r);
        for (let c = 1; c <= totalCols; c += 1) {
          const cell = row.getCell(c);
          cell.fill = headerFill;
          cell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
          };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
          };
        }
      }

      const thinBorder = {
        top: { style: "thin" as const, color: { argb: "FFD1D5DB" } },
        left: { style: "thin" as const, color: { argb: "FFD1D5DB" } },
        bottom: { style: "thin" as const, color: { argb: "FFD1D5DB" } },
        right: { style: "thin" as const, color: { argb: "FFD1D5DB" } },
      };

      const firstDataRow = 3;
      const lastDataRow = Math.max(2, worksheet.rowCount);

      for (let r = 1; r <= lastDataRow; r += 1) {
        for (let c = 1; c <= totalCols; c += 1) {
          worksheet.getRow(r).getCell(c).border = thinBorder;
        }
      }

      for (let r = firstDataRow; r <= lastDataRow; r += 1) {
        const taskCell = worksheet.getRow(r).getCell(1);
        taskCell.font = { bold: true, color: { argb: "FF111827" } };
        taskCell.alignment = { horizontal: "left", vertical: "middle" };
      }

      worksheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2, column: totalCols },
      };

      const sanitizedProjectName = (project.name || "project")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      const filename = `${sanitizedProjectName || "project"}-planning-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export XLSX:", error);
      alert("Échec de l'export XLSX.");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="task-modal-overlay"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="task-modal" onClick={(e) => e.stopPropagation()}>
        <div className="task-modal-header">
          <h3>Tasks for {project.name}</h3>
        </div>

        <div className="task-modal-body">
          <div className="task-summary">
            <span>Total tasks: {tasks.length}</span>
            <span>
              Weekly planning view ({scheduleWeeks.length} week columns)
            </span>
          </div>

          <div className="task-actions-top">
            <button
              className="task-export-button"
              onClick={() => void exportTasksToXlsx()}
            >
              Export XLSX
            </button>
          </div>

          <div className="task-weekly-breakdown">
            <h4>Task weekly schedule</h4>
            {scheduleWeeks.length === 0 ? (
              <div className="task-empty">
                Set project start and end dates to display the weekly grid.
              </div>
            ) : (
              <div className="task-grid-wrapper">
                <div
                  className="task-schedule-grid"
                  style={{
                    gridTemplateColumns: `280px repeat(${scheduleWeeks.length}, minmax(90px, 1fr))`,
                  }}
                >
                  <div className="task-grid-cell task-grid-header task-grid-month-header task-grid-sticky">
                    Task
                  </div>
                  {monthGroups.map((group) => (
                    <div
                      key={`month-${group.startIndex}`}
                      className="task-grid-cell task-grid-header task-grid-month-header"
                      style={{ gridColumn: `${group.startIndex + 2} / span ${group.span}` }}
                    >
                      {group.label}
                    </div>
                  ))}

                  <div className="task-grid-cell task-grid-header task-grid-week-header task-grid-sticky">
                    Week
                  </div>
                  {scheduleWeeks.map((week) => (
                    <div
                      key={toIsoDate(week)}
                      className="task-grid-cell task-grid-header task-grid-week-header"
                    >
                      {toWeekRangeLabel(week)}
                    </div>
                  ))}

                  {tasks.map((task) => (
                    <React.Fragment key={task.id}>
                      <div className="task-grid-cell task-grid-task-name task-grid-sticky">
                        <div className="task-grid-task-main">
                          <input
                            type="text"
                            value={task.name}
                            onChange={(e) =>
                              updateTask(task.id, { name: e.target.value })
                            }
                            placeholder="Task name"
                            className="task-grid-name-input"
                          />
                          <select
                            className="task-grid-status-select"
                            value={task.status || TaskStatus.TODO}
                            onChange={(e) =>
                              updateTask(task.id, {
                                status: e.target.value as TaskStatus,
                              })
                            }
                          >
                            {Object.values(TaskStatus).map((status) => (
                              <option key={status} value={status}>
                                {TASK_STATUS_LABELS[status]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          className="task-delete-button"
                          onClick={() => deleteTask(task.id)}
                          title="Delete task"
                        >
                          Delete
                        </button>
                        {onMoveTask && availableProjects.length > 0 && (
                          <div className="task-grid-task-move">
                            <select
                              className="task-grid-move-select"
                              value={taskMoveTargets[task.id] || String(availableProjects[0]?.id ?? "")}
                              onChange={(e) =>
                                setTaskMoveTargets((prev) => ({
                                  ...prev,
                                  [task.id]: e.target.value,
                                }))
                              }
                            >
                              {availableProjects.map((candidate) => (
                                <option key={candidate.id} value={String(candidate.id)}>
                                  {candidate.name}
                                </option>
                              ))}
                            </select>
                            <button
                              className="task-move-button"
                              onClick={() => moveTaskToProject(task)}
                              title="Move task to another project"
                            >
                              Move
                            </button>
                          </div>
                        )}
                      </div>
                      {scheduleWeeks.map((week) => {
                        const action = getTaskCellAction(task, week);
                        const title = getTaskCellTitle(task, week);

                        return (
                          <div
                            key={`${task.id}-${toIsoDate(week)}`}
                            className={`task-grid-cell task-week-cell clickable action-${action} ${
                              isTaskActiveInWeek(task, week) ? "active" : ""
                            }`}
                            title={title}
                            role="button"
                            tabIndex={0}
                            aria-label={title}
                            onClick={(e) =>
                              openWeekEditMenu(
                                task.id,
                                week,
                                e.clientX,
                                e.clientY,
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                const target = e.currentTarget;
                                const rect = target.getBoundingClientRect();
                                openWeekEditMenu(
                                  task.id,
                                  week,
                                  rect.left + rect.width / 2,
                                  rect.top + rect.height / 2,
                                );
                              }
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="task-actions-bottom">
            <button className="task-add-button" onClick={addTask}>
              + Add Task
            </button>
          </div>
        </div>

        {weekEditMenu && (
          <div
            className="task-week-menu"
            style={{ left: weekEditMenu.x, top: weekEditMenu.y }}
          >
            <button
              className="task-week-menu-item"
              onClick={() =>
                applyStartHere(weekEditMenu.taskId, weekEditMenu.week)
              }
            >
              Start here
            </button>
            <button
              className="task-week-menu-item"
              onClick={() =>
                applyEndHere(weekEditMenu.taskId, weekEditMenu.week)
              }
            >
              End here
            </button>
            <button
              className="task-week-menu-item task-week-menu-cancel"
              onClick={() => setWeekEditMenu(null)}
            >
              Cancel
            </button>
          </div>
        )}

        <div className="task-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="save-button"
            onClick={() => onSave(tasks.map(normalizeTaskWeekStartToMonday))}
          >
            Save Tasks
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectTasksModal;
